import React, {
    useContext, useMemo, useState, useEffect
} from 'react';
import { TranslatorContext } from '@jutro/locale';
import { Button } from '@jutro/components';
import { SgDateUtil, SgPaymentUtils } from 'saga-modules';
import _ from 'lodash';
import { ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import CepConstants from 'sg-cep-constants';
import SgScrollUtil from 'sg-scroll-util';
import SgPaymentTileDetails from './sg-payment-tile-details/SgPaymentTileInvoicesDetails';
import SgPaymentTileInstrumentDetails from './sg-payment-tile-details/SgPaymentTileInstrumentDetails';
import messages from './SgPolicyPaymentTile.messages';
import SgOutstandingPayments from './sg-payment-tile-details/SgOutstandingPayments';

function SgPolicyPaymentTile(props) {
    const {
        id,
        isOpened,
        selectPolicy,
        policy,
        isInitialPageRender
    } = props;
    const viewModelService = useContext(ViewModelServiceContext);

    const [paymentDetails, setPaymentDetails] = useState(undefined);
    const [paymentInstrumentVM, setPaymentInstrumentVM] = useState(SgPaymentUtils.createPaymentInstrumentVM(viewModelService));
    const [showPaymentInstrument, setShowPaymentInstrument] = useState(false);
    const [showPaymentDetailsContent, setShowPaymentDetailsContent] = useState(true);
    const [toPayFilterSelected, setToPayFilterSelected] = useState(true);
    const [paidFilterSelected, setPaidFilterSelected] = useState(true);
    const [editAutoRenewal, setEditAutoRenewal] = useState(false);
    const [arrearsPaid, setArrearsPaid] = useState(false);
    const translator = useContext(TranslatorContext);

    const buildCustomerDetails = useMemo(() => {
        const basicInfo = policy.basicInformation;
        return SgPaymentUtils.buildCustomerDataForIframe(basicInfo.policyAddress, basicInfo.policyEmail,
            basicInfo.policyFirstName, basicInfo.policyLastName, true);
    }, []);

    const commonProps = useMemo(() => {
        return {
            id,
            paymentDetails,
            toPayFilterSelected,
            setToPayFilterSelected,
            paidFilterSelected,
            setPaidFilterSelected,
            setShowPaymentInstrument,
            paymentInstrumentVM,
            setPaymentInstrumentVM,
            editAutoRenewal,
            setEditAutoRenewal,
            setPaymentDetails,
            policyNumber: policy.basicInformation.policyNumber,
            invoiceStreams: policy.invoiceStreams,
            isInRenewalCycle: policy.basicInformation.isInRenewalCycle,
            isRenewable: policy.basicInformation.isRenewable,
            productCode: policy.basicInformation.productCode,
            customerDetails: buildCustomerDetails
        };
    }, [editAutoRenewal, id, paidFilterSelected, paymentDetails, paymentInstrumentVM, toPayFilterSelected]);

    useEffect(() => {
        if (isOpened || arrearsPaid) {
            SgPaymentUtils.retrieveAndSetPaymentDetails(commonProps);
            if (!arrearsPaid) {
                setShowPaymentDetailsContent(true);
            }
        } else {
            setShowPaymentInstrument(false);
            setToPayFilterSelected(true);
            setPaidFilterSelected(true);
            setEditAutoRenewal(false);
            setShowPaymentDetailsContent(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpened, arrearsPaid]);

    const paymentDetailsContent = useMemo(() => {
        if (!isInitialPageRender.current) {
            SgScrollUtil.scrollToElementById(`policyPaymentContainer_${id}`, null, -50);
        }
        return (
            <div id={`policyPaymentDetails_${id}`} className="sg-policy-tile-expanding-details-container">
                {!showPaymentInstrument && (<SgPaymentTileDetails {...commonProps} />)}
                {showPaymentInstrument && (<SgPaymentTileInstrumentDetails {...commonProps} />)}
            </div>
        );
    }, [id, showPaymentInstrument, commonProps]);

    const outstandingPaymentsContent = useMemo(() => {
        return paymentDetails && (
            <SgOutstandingPayments
                policyDetails={policy}
                paymentDetails={paymentDetails}
                setShowPaymentDetailsContent={setShowPaymentDetailsContent}
                setShowPaymentInstrument={setShowPaymentInstrument}
                showPaymentInstrument={showPaymentInstrument}
                showPaymentDetailsContent={showPaymentDetailsContent}
                arrearsPaid={arrearsPaid}
                setArrearsPaid={setArrearsPaid}
                customerDetails={buildCustomerDetails}
            />
        );
    }, [arrearsPaid, paymentDetails, showPaymentDetailsContent]);

    const showOutstandingPayments = useMemo(() => {
        return arrearsPaid || (paymentDetails && _.filter(
            paymentDetails.invoiceSummary,
            (invoice) => invoice.invoiceStatus === CepConstants.PAID_STATUS.DUE && invoice.paidStatus === CepConstants.PAID_STATUS.UNPAID
        ).length > 0);
    }, [arrearsPaid, paymentDetails, showPaymentDetailsContent]);

    return (
        <div id={`policyPaymentContainer_${id}`} className="sg-policy-summary sg-policy-tile">
            <div id={`policyPaymentMain_${id}`} className={`sg-policy-tile-details-container ${policy.basicInformation.productCode}`}>
                <h3 id={`header_${id}`} className="sg-policy-description">
                    {policy.basicInformation.policyHeader.header}
                    <span className="sg-policy-registration-number">
                        <span className="separator" />
                        {policy.basicInformation.policyHeader.subHeader}
                    </span>
                </h3>
                <div className="sg-policy-info">
                    <h3>{translator(messages.PolicyNumber)}</h3>
                    <h3 id={`policy_${id}`}>{policy.basicInformation.policyNumber}</h3>
                </div>
                <div className="sg-clear-both" />
                <div className="sg-policy-info">
                    <h3>{translator(messages.StartDate)}</h3>
                    <h3 id={`policySummary_${id}`}>
                        {SgDateUtil.getDateWithFullMonthName(policy.basicInformation.startDate)}
                    </h3>
                </div>
                <div className="sg-clear-both" />
            </div>
            <div id={`buttonContainer_${id}`} className="sg-policy-tile-button-container">
                <br className="sg-hide-for-phone-landscape-up" />
                {
                    !isOpened && (
                        <Button
                            id={`viewPaymentsButton_${id}`}
                            onClick={() => selectPolicy(policy.policyNumber_Ext)}
                            className="sgButton"
                        >
                            {translator(messages.ViewPayments)}
                        </Button>
                    )
                }
            </div>
            <div className="sg-clear-both" />
            { isOpened && showOutstandingPayments && outstandingPaymentsContent }
            { isOpened && showPaymentDetailsContent && paymentDetailsContent }
        </div>
    );
}

export default SgPolicyPaymentTile;
